import { ErrorOutline, OpenInNew } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useState } from "react";
import { useQueryParam, withDefault, NumberParam } from "use-query-params";

import {
  GlobalOptOutSummaryQuery,
  TrackedJobStatus,
} from "~frontend/__generated__/graphql";
import { palette } from "~frontend/theme";

import { FlexBox } from "../FlexBox";
import Loading from "../Loading";
import Table from "../Table";
import { DeletedOptOutListDialog } from "./DeletedOptOutListDialog";

export type DeleteOptOutListJob =
  GlobalOptOutSummaryQuery["globalOptOutSummary"]["deleteOptOutListJobs"][number];

export const DeletedOptOutListTable = ({
  deleteOptOutListJobs,
}: {
  deleteOptOutListJobs: DeleteOptOutListJob[];
}): ReactElement => {
  const [page, setPage] = useQueryParam(
    "DELETED-page",
    withDefault(NumberParam, 0)
  );
  const [pageSize, setPageSize] = useQueryParam(
    "DELETED-pageSize",
    withDefault(NumberParam, 10)
  );

  const [detailsOpenForJob, setDetailsOpenForJob] =
    useState<DeleteOptOutListJob | null>(null);

  const jobFailed = (job: DeleteOptOutListJob) =>
    job.status === TrackedJobStatus.Failed;

  const jobInProgress = (job: DeleteOptOutListJob) =>
    !jobFailed(job) && job.status !== TrackedJobStatus.Done;

  return (
    <>
      <Table<DeleteOptOutListJob>
        columns={[
          {
            key: "name",
            title: "List",
            renderValue: row => (
              <FlexBox
                sx={{
                  color: jobInProgress(row)
                    ? palette.darkGray
                    : jobFailed(row)
                      ? palette.red
                      : undefined,
                  fontStyle: jobInProgress(row) ? "italic" : undefined,
                  gap: "10px",
                  paddingY: "6px",
                }}
              >
                {jobInProgress(row) ? (
                  <Loading size={20} style={{ marginTop: 0 }} />
                ) : null}
                {jobFailed(row) ? (
                  <ErrorOutline sx={{ fontSize: "16px" }} />
                ) : null}
                {jobFailed(row) ? (
                  <Box sx={{ color: palette.red }}>
                    <Typography>{row.args.list.name}</Typography>
                    <Typography variant="body3">{row.errorMessage}</Typography>
                  </Box>
                ) : (
                  row.args.list.name
                )}
              </FlexBox>
            ),
          },
          {
            key: "addedBy",
            title: "Added By",
            sx: { width: 150 },
            renderValue: row => (
              <Box
                sx={{
                  color: jobInProgress(row) ? palette.darkGray : undefined,
                  fontStyle: jobInProgress(row) ? "italic" : undefined,
                }}
              >
                {row.args.list.createdByUser?.fullName}
              </Box>
            ),
          },
          {
            key: "addedAt",
            title: "Added At",
            sx: { width: 150 },
            renderValue: row => (
              <Box
                sx={{
                  color: jobInProgress(row) ? palette.darkGray : undefined,
                  fontStyle: jobInProgress(row) ? "italic" : undefined,
                }}
              >
                {DateTime.fromMillis(row.args.list.createdAt).toLocaleString()}
              </Box>
            ),
          },
          {
            key: "deletedAt",
            title: "Deleted at",
            sx: { width: 150 },
            renderValue: row =>
              !jobFailed(row) &&
              row.finishedAt &&
              DateTime.fromMillis(row.finishedAt).toLocaleString(),
          },
          {
            key: "count",
            title: "Opt Outs",
            sx: { width: 100 },
            renderValue: row => (
              <Box
                sx={{
                  color: jobInProgress(row) ? palette.darkGray : undefined,
                }}
              >
                <strong>{row.args.list.count.toLocaleString()}</strong>
              </Box>
            ),
          },
        ]}
        rowActions={row =>
          jobInProgress(row)
            ? []
            : [
                {
                  id: "view-details",
                  type: "button",
                  icon: <OpenInNew />,
                  tooltipLabel: "View list details",
                  onClick: list => setDetailsOpenForJob(list),
                },
              ]
        }
        rows={deleteOptOutListJobs.slice(
          page * pageSize,
          (page + 1) * pageSize
        )}
        rowsPerPage={pageSize}
        page={page}
        onChangePage={setPage}
        onChangeRowsPerPage={setPageSize}
        totalCount={deleteOptOutListJobs.length}
      />
      {detailsOpenForJob && (
        <DeletedOptOutListDialog
          listName={detailsOpenForJob.args.list.name}
          deletionSummary={detailsOpenForJob.result!}
          onClose={() => setDetailsOpenForJob(null)}
        />
      )}
    </>
  );
};
